/*
 * AQUORIX Walk-In Manager
 * File: WalkInManager.tsx
 * Path: /Users/larrymclean/CascadeProjects/aquorix-frontend/src/features/dashboard/pages/WalkInManager.tsx
 * Description:
 *   Faithful React port of the approved Walk-In Manager design.
 *
 * Author: Larry McLean
 * Created: 2026-07-16
 * Version: 1.0.16
 * Status: ACTIVE
 *
 * Change Log (append-only):
 *   - 2026-07-17: v1.0.1 - I1b-1 types and imports for live schedule boot.
 *   - 2026-07-16: v1.0.0 - Unit 1 scaffold: stylesheet import, state, fixture, helpers, return null.
 *   - 2026-07-19: v1.0.2 - Slice 4A records cash, terminal, or hold settlement state.
 *   - 2026-07-20: v1.0.3 - Completion settlement copy reports time-sensitive holds without asserting payment.
 *   - 2026-07-20: v1.0.4 - Completion payment tick reports time-sensitive holds without asserting settlement.
 *   - 2026-07-21: v1.0.5 - Separate booking-create failures from settlement-record recovery.
 *   - 2026-07-21: v1.0.6 - Audit completion ticks against create and settlement response truth.
 *   - 2026-07-21: v1.0.7 - AQX-077 Align customer identity messaging with the email-or-phone contract.
 *   - 2026-07-21: v1.0.8 - Use the reviewed Diver 1 contact as the booking transaction contact.
 *   - 2026-07-21: v1.0.9 - P0 restoration: make Diver 1 the transaction identity and remove the Step 1 save gate.
 *   - 2026-07-21: v1.0.10 - Stop sending certification level through the constrained certification-status workflow field.
 *   - 2026-08-22: v1.0.11 - Carry Viking identity, emergency-contact, and conditional guardian fields through Book Now.
 *   - 2026-08-22: v1.0.12 - Display required identity, emergency-contact, and activity-date guardian fields.
 *   - 2026-08-22: v1.0.13 - Gate booking creation with the shared Viking participant-readiness contract.
 *   - 2026-08-26: v1.0.14 - Collect booking-specific accommodation and room number for Egypt Manifest output.
 *   - 2026-08-27: v1.0.15 - Require complete Egypt Manifest data and explicit counter review before booking.
 *   - 2026-08-27: v1.0.16 - Shorten required markers and keep reusable-identity strategy internal.
 */

import { useEffect, useState } from "react"
import "./WalkInManager.css"
import { apiFetch } from "../../../utils/api"
import { NATIONALITIES, type Nationality, nationalityLabel } from "../../../reference/nationalities"



type WalkInOperator = {
  name: string
  currency: string
  defaultCapacity: number | null
  today?: string | null
}

type WalkInSession = {
  id: string
  type: string
  title: string
  date: string
  time: string
  meet?: string
  meetLoc?: string
  guide?: string
  cat?: string
  vessel?: string
  agency?: string
  level?: string
  total: number
  remaining: number
  priceMinor: number
  currency?: string
  maxDepth?: number
  diveLevel: string
}

type WalkInCustomer = {
  guest_id?: string | null
  first_name?: string
  last_name?: string
  preferred_name?: string
  dob?: string
  nationality?: string
  email?: string
  phone?: string
  gender?: string
  id_type?: string
  id_number?: string
  emergency_contact_name?: string
  emergency_contact_relationship?: string
  emergency_contact_phone?: string
  emergency_contact_phone_secondary?: string
  guardian_name?: string
  guardian_relationship?: string
  guardian_phone?: string
  guardian_id_document_type?: string
  guardian_id_document_number?: string
  guardian_nationality_code?: string
  isNew?: boolean
  last_booking_date?: string | null
}

type WalkInItineraryItem = {
  uid: string
  ref: string
  type: string
  title: string
  date: string
  time: string
  waitlist?: boolean
  priceMinor: number
  currency?: string
  diveLevel?: string | null
  maxDepth?: number | null
  guide?: string
  pax: number
  paxEdited?: boolean
}

type WalkInPromo = {
  kind: "pct" | "fixed"
  value: number
  label?: string
}

type WalkInDoneLine = {
  uid: string
  title: string
  pax: number
  time: string
  priceMinor: number
  waitlist?: boolean
}

type WalkInDoneDay = {
  date: string
  lines: WalkInDoneLine[]
}

type WalkInDoneTick = {
  cls: string
  name: string
  sub: string
}

type WalkInDoneData = {
  ref: string
  nm: string
  full: string
  ini: string
  email: string
  phone: string
  nationality: string
  pax: number
  days: WalkInDoneDay[]
  ticks: WalkInDoneTick[]
  totals: {
    subtotal: number
    promoAmt: number
    grand: number
  }
  payMethod: string
}

type WalkInCreateResult = {
  bookingId: number | string
  bookingStatus: string
  paymentStatus: string
  paymentMethod: string
  source: string
  holdExpiresAt: string | null
}

type WalkInSettlementResult = {
  bookingStatus: string
  paymentStatus: string
  paymentMethod: string
  paidAt: string | null
}

const EMPTY_OPERATOR: WalkInOperator = { name:"", currency:"JOD", defaultCapacity: null, today: null };
/* NATIONALITY VOCABULARY
 * Runtime source: GET /api/v1/nationalities.
 * Fallback source: shared src/reference/nationalities.ts.
 */;
const DIFFICULTY = [
  { v:"all_levels",   label:"All Levels",   ord:0, depth:18, note:"no gate" },
  { v:"beginner",     label:"Beginner",     ord:1, depth:18 },
  { v:"intermediate", label:"Intermediate", ord:2, depth:30 },
  { v:"advanced",     label:"Advanced",     ord:3, depth:40 },
  { v:"technical",    label:"Technical",    ord:4, depth:99 },
];
const diff = (v: string) => DIFFICULTY.find(d=>d.v===v) || {};

/* The diver's card. maxDiff = the hardest dive this certification may lead with. */
const LEVELS = [
  { v:"discover_scuba", label:"Discover Scuba / Try Dive", short:"Discover Scuba", depth:12, maxDiff:1 },
  { v:"open_water",     label:"Open Water \u00b7 18 m",     short:"Open Water",     depth:18, maxDiff:1 },
  { v:"advanced",       label:"Advanced Open Water \u00b7 30 m", short:"Advanced",  depth:30, maxDiff:2 },
  { v:"deep",           label:"Deep \u00b7 40 m",           short:"Deep",           depth:40, maxDiff:3 },
  { v:"technical",      label:"Technical \u00b7 40 m+",     short:"Technical",      depth:99, maxDiff:4 },
];
const lvl = (v: string) => LEVELS.find(l=>l.v===v) || {};

/* ============ HELPERS ============ */
const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
const MON3=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const WD=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const div = (c: string | null | undefined) => (String(c||"JOD")==="JOD"?1000:100);
const money = (m: number, currency: string) => `${(m/div(currency)).toFixed(2)} ${currency}`;
const iso = (d: Date) => d.toISOString().slice(0,10);
const dt  = (s: string) => new Date(s+"T12:00:00Z");
function addDays(s: string,n: number){ const d=dt(s); d.setUTCDate(d.getUTCDate()+n); return iso(d); }
function t12(t: string | null | undefined){ if(!t) return ""; const [h,m]=t.split(":").map(Number); const ap=h<12?"a":"p"; const hh=((h+11)%12)+1; return `${hh}:${String(m).padStart(2,"0")}${ap}`; }
function dparts(s: string){ const d=dt(s); return {wd:WD[d.getUTCDay()], dnum:d.getUTCDate(), mon:MON3[d.getUTCMonth()]}; }
function rosterName(c: WalkInCustomer | null,i: number){ if(!c) return `Diver ${i+1}`;   const p = c.preferred_name || c.first_name; return i===0 ? `${p} ${(c.last_name||"")[0]||""}.` : `Diver ${i+1}`; }

function mapLookupCustomerToWalkInCustomer(row: any): WalkInCustomer {
  return {
    guest_id: row.guest_id != null ? String(row.guest_id) : null,
    first_name: row.first_name || "",
    last_name: row.last_name || "",
    preferred_name: row.preferred_name || "",
    dob: row.date_of_birth || row.dob || "",
    nationality: row.nationality_code || row.nationality || "",
    email: row.email || "",
    phone: row.phone_number || row.phone || "",
    gender: row.gender || "",
    id_type: row.id_document_type || row.id_type || "passport",
    id_number: row.id_document_number || row.id_number || "",
    emergency_contact_name: row.emergency_contact_name || "",
    emergency_contact_relationship:
      row.emergency_contact_relationship || "",
    emergency_contact_phone: row.emergency_contact_phone || "",
    emergency_contact_phone_secondary:
      row.emergency_contact_phone_secondary || "",
    guardian_name: row.guardian_name || "",
    guardian_relationship: row.guardian_relationship || "",
    guardian_phone: row.guardian_phone || "",
    guardian_id_document_type:
      row.guardian_id_document_type || "passport",
    guardian_id_document_number:
      row.guardian_id_document_number
      || row.guardian_passport_number
      || "",
    guardian_nationality_code:
      row.guardian_nationality_code || "",
    last_booking_date: row.last_booking_date || null,
  };
}

function mapScheduleSessionToWimSession(session: any): WalkInSession {
  return {
    id: String(session.id || ""),
    type: String(session.type || ""),
    title: String(session.title || ""),
    date: String(session.session_date || ""),
    time: String(session.start_time || ""),
    meet: session.meet_time || undefined,
    meetLoc: session.meet_location || undefined,
    guide: session.guide_name || undefined,
    vessel: session.vessel_name || undefined,
    cat: session.dive_category || session.metadata?.dive_category || undefined,
    agency: session.metadata?.certification_agency || undefined,
    level: session.metadata?.course_level || undefined,
    total: session.capacity_total,
    remaining: session.capacity_remaining,
    priceMinor: Number(session.price_from_minor || 0),
    currency: String(session.currency || "JOD").trim().toUpperCase(),
    maxDepth: undefined,
    diveLevel: "",
  };
}

export default function WalkInManager() {
  const [party, setPartyState] = useState(1)
  const [itinerary, setItinerary] = useState<WalkInItineraryItem[]>([])
  const [customer, setCustomer] = useState<WalkInCustomer | null>(null)
  const [operator, setOperator] = useState<WalkInOperator>(EMPTY_OPERATOR)
  const [today, setToday] = useState<string | null>(null)
  const [sessions, setSessions] = useState<WalkInSession[]>([])
  const [nationalities, setNationalities] = useState<Nationality[]>(NATIONALITIES)
  const [calendarRequestSeq, setCalendarRequestSeq] = useState(0)
  const availOn = (d: string | null) => d ? sessions.filter(s=>s.date===d) : []
  function natLabel(code: string){ return nationalityLabel(code, nationalities); }
  const [weekOffset, setWeekOffset] = useState(0)
  const [selDate, setSelDate] = useState<string | null>(null)

  useEffect(() => {
    let alive = true;

    async function bootNationalities() {
      try {
        const response = await apiFetch("/api/v1/nationalities");
        const data = await response.json();
        if(!alive) return;
        if(Array.isArray(data?.nationalities)) setNationalities(data.nationalities);
      } catch {
        /* Silent fallback: keep the bundled fixture. */
      }
    }

    async function bootSchedule() {
      const firstResponse = await apiFetch("/api/v1/dashboard/schedule");
      const first = await firstResponse.json();
      const operatorPayload = first?.operator || {};
      const operatorToday = operatorPayload.today || null;

      if(!operatorToday) return;

      const nextOperator: WalkInOperator = {
        name: operatorPayload.name || "",
        currency: operatorPayload.currency || "JOD",
        defaultCapacity: operatorPayload.operator_default_capacity ?? null,
        today: operatorToday,
      };

      const secondResponse = await apiFetch(`/api/v1/dashboard/schedule?week_start=${encodeURIComponent(operatorToday)}`);
      const second = await secondResponse.json();

      if(!alive) return;
      setOperator(nextOperator);
      setToday(operatorToday);
      setSelDate(operatorToday);
      setWeekOffset(0);
      setSessions(Array.isArray(second?.sessions) ? second.sessions.map(mapScheduleSessionToWimSession) : []);
    }

    bootNationalities();
    bootSchedule();
    return () => { alive = false; };
  }, []);
  const [payMethod, setPayMethod] = useState("")
  const [createErr, setCreateErr] = useState<string>("")
  const [creating, setCreating] = useState(false)
  const [createdBookingId, setCreatedBookingId] = useState<number | string | null>(null)
  const [settlementFailure, setSettlementFailure] = useState<{bookingId: number | string; message: string} | null>(null)
  const [promo, setPromo] = useState<WalkInPromo | null>(null)
  const [promoInput, setPromoInput] = useState("")
  const [calBase, setCalBase] = useState<string | null>(null)
  const [calOpen, setCalOpen] = useState(false)
  const [doneData, setDoneData] = useState<WalkInDoneData | null>(null)
  const [step1Mode, setStep1Mode] = useState<"search" | "match" | "capture" | "chip">("search")
  const [lookup, setLookup] = useState("")
  const [lookupResults, setLookupResults] = useState<WalkInCustomer[]>([])
  const [newCustomerDraft, setNewCustomerDraft] = useState({
    fn: "",
    ln: "",
    pn: "",
    dob: "",
    nat: "",
    gen: "",
    em: "",
    ph: "",
    idt: "passport",
    idn: "",
  })
  const [manifestLevels, setManifestLevels] = useState<Record<number, string>>({})
  const [medicalStates, setMedicalStates] = useState<Record<number, "clear" | "warn" | "stop">>({})
  const [manifestDrafts, setManifestDrafts] = useState<Record<number, Record<string, string | boolean>>>({})
  const [identityReviewed, setIdentityReviewed] = useState<Record<number, boolean>>({})
  const [matchedDivers, setMatchedDivers] = useState<Record<number, WalkInCustomer>>({})
  const [diverLookupTerms, setDiverLookupTerms] = useState<Record<number, string>>({})
  const [diverLookupResults, setDiverLookupResults] = useState<Record<number, WalkInCustomer[]>>({})

  function weekRangeForOffset(offset: number){
    if(!today) return null;
    const start = addDays(today, offset*7);
    const end = addDays(start, 6);
    return { start, end, days: Array.from({length:7},(_,i)=>addDays(start,i)) };
  }

  async function fetchCalendarRange(start: string, end: string, requestSeq: number){
    const response = await apiFetch(`/api/v1/dashboard/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
    const data = await response.json();
    setCalendarRequestSeq((current) => {
      if(current === requestSeq){
        setSessions(Array.isArray(data?.sessions) ? data.sessions.map(mapScheduleSessionToWimSession) : []);
      }
      return current;
    });
  }

  function weekDays(){ const range = weekRangeForOffset(weekOffset); return range ? range.days : []; }

  function setParty(d: number){
    const nextParty = Math.max(1, Math.min(12, party+d));
    setPartyState(nextParty);
    setItinerary((lines) => lines.map((line) => {
      if(line.pax > nextParty) return { ...line, pax: nextParty };
      if(!line.paxEdited) return { ...line, pax: nextParty };
      return line;
    }));
  }

function setLinePax(uid: string, d: number){
  const line = itinerary.find((item) => item.uid === uid);
  if(!line) return;
  const nextPax = Math.max(1, Math.min(12, line.pax+d));
  if(nextPax > party) setPartyState(nextPax);
  setItinerary((lines) => lines.map((item) => item.uid === uid ? { ...item, pax: nextPax, paxEdited: true } : item));
}

function shiftWeek(d: number){
  const nextWeekOffset = weekOffset + d;
  const range = weekRangeForOffset(nextWeekOffset);
  if(!range) return;
  const nextRequestSeq = calendarRequestSeq + 1;
  setCalendarRequestSeq(nextRequestSeq);
  fetchCalendarRange(range.start, range.end, nextRequestSeq);
  setWeekOffset(nextWeekOffset);
  if(!selDate || !range.days.includes(selDate)) setSelDate(range.days[0]);
}

function pickDate(d: string){ setSelDate(d); }

function openCal(){
  if(!selDate) return;
  const nextCalBase = selDate.slice(0,7)+"-01";
  const base = dt(nextCalBase);
  const start = iso(new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1)));
  const end = iso(new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth()+1, 0)));
  const nextRequestSeq = calendarRequestSeq + 1;
  setCalBase(nextCalBase);
  setCalendarRequestSeq(nextRequestSeq);
  fetchCalendarRange(start, end, nextRequestSeq);
  setCalOpen(true);
}
function closeCal(start?: string, end?: string){
  setCalOpen(false);
  const range = start && end ? { start, end } : weekRangeForOffset(weekOffset);
  if(!range) return;
  const nextRequestSeq = calendarRequestSeq + 1;
  setCalendarRequestSeq(nextRequestSeq);
  fetchCalendarRange(range.start, range.end, nextRequestSeq);
}
function calShift(n: number){
  if(!calBase) return;
  const d = dt(calBase);
  d.setUTCMonth(d.getUTCMonth()+n);
  const nextCalBase = iso(d).slice(0,7)+"-01";
  const base = dt(nextCalBase);
  const start = iso(new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1)));
  const end = iso(new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth()+1, 0)));
  const nextRequestSeq = calendarRequestSeq + 1;
  setCalBase(nextCalBase);
  setCalendarRequestSeq(nextRequestSeq);
  fetchCalendarRange(start, end, nextRequestSeq);
}

function renderCal(){
  if(!calBase) return null;
  const base = dt(calBase);
  const first = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
  const lead = first.getUTCDay();
  const days = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth()+1, 0)).getUTCDate();

  return (
    <>
      <div className="wim-calwd">{WD.map(w=><span key={w}>{w}</span>)}</div>
      <div className="wim-calgrid">
        {Array.from({length:lead},(_,i)=><div key={`empty-${i}`} className="wim-cal-cell wim-empty"></div>)}
        {Array.from({length:days},(_,i)=>{
          const d = i + 1;
          const s = iso(new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), d)));
          return calCell(s, d);
        })}
      </div>
    </>
  );
}

function calCell(s: string, d: number){
  if(!today) return null;
  const past = s < today, isToday = s === today, sel = s === selDate;
  if(past) return <div key={s} className="wim-cal-cell wim-past"><span className="wim-cal-num">{d}</span></div>;
  const list = availOn(s);
  if(!list.length) return (
    <div key={s} className={`wim-cal-cell wim-none ${isToday?'wim-today':''} ${sel?'wim-sel':''}`.trim()} onClick={() => selCalDay(s)} tabIndex={0} role="button">
      <span className="wim-cal-num">{d}</span><span className="wim-cal-dash">&ndash;</span>
    </div>
  );
  const capacityList = list.filter(x=>x.total != null && x.remaining != null);
  const seats = capacityList.reduce((a,x)=>a+x.total,0), avail = capacityList.reduce((a,x)=>a+x.remaining,0);
  const fill = capacityList.length && seats ? 1-avail/seats : 0;
  const wpct = Math.max(8, Math.round(fill*100));
  const wait = capacityList.length ? avail===0 : false;
  const state = wait ? 'wim-wait' : (fill>0.66 ? 'wim-hi' : '');
  const cheapest = list.slice().sort((a,b)=>a.priceMinor-b.priceMinor)[0];
  const from = cheapest?.priceMinor || 0;
  return (
    <div key={s} className={`wim-cal-cell ${state} ${isToday?'wim-today':''} ${sel?'wim-sel':''}`.trim()} onClick={() => selCalDay(s)} tabIndex={0} role="button" aria-label={`${s}, ${list.length} runs, from ${money(from, cheapest?.currency || operator.currency)}`}>
      <span className="wim-cal-num">{d}</span>
      <span className="wim-cal-bar"><i style={{width:`${wpct}%`}}></i></span>
      <span className="wim-cal-price">{(from/div(cheapest?.currency || operator.currency)).toFixed(0)}</span>
    </div>
  );
}

function selCalDay(s: string){
  if(!today) return;
  if(s < today) return;
  setSelDate(s);
  const start = dt(today), target = dt(s);
  let nextOffset = Math.floor((target.getTime() - start.getTime()) / (7*24*3600*1000));
  let range = weekRangeForOffset(nextOffset);
  if(range && !range.days.includes(s)){
    nextOffset = Math.round((target.getTime() - start.getTime())/(7*24*3600*1000));
    range = weekRangeForOffset(nextOffset);
  }
  setWeekOffset(nextOffset);
  if(range) closeCal(range.start, range.end);
  else closeCal();
}

function renderDates(){
  return weekDays().map(d=>{
    const p = dparts(d), n = availOn(d).length;
    return (
      <div key={d} className={`wim-daypill ${d===selDate?'wim-sel':''}`} onClick={() => pickDate(d)} tabIndex={0} role="button">
        {d===today ? <div className="wim-tdy">Today</div> : <div className="wim-dow">{p.wd}</div>}
        <div className="wim-dnum">{p.dnum}</div>
        <div className="wim-dow" style={{opacity:n?'.9':'.4'}}>{n ? n+(n===1?' run':' runs') : '—'}</div>
      </div>
    );
  });
}

const inItin = (ref: string) => itinerary.some(l=>l.ref===ref);

function renderSessions(){
  const list = availOn(selDate);
  if(!list.length){
    return <div className="wim-emptyday">Nothing has been locked in the schedule for this day. If a diver is ready, create dive session in Shedule then return here to assign walkin itinerary.</div>;
  }
  const dives = list.filter(s=>s.type==='dive'), courses = list.filter(s=>s.type==='course');
  return (
    <>
      {dives.length ? <><div className="wim-parthead">Dives</div>{dives.map(card)}</> : null}
      {courses.length ? <><div className="wim-parthead">Courses</div>{courses.map(card)}</> : null}
    </>
  );
}

function card(s: any){
  const on = inItin(s.id), full = s.remaining === 0;
  const line = itinerary.find(l=>l.ref===s.id);
  const meta = [ t12(s.time), s.meet?`meet ${t12(s.meet)}`:null, s.meetLoc, s.vessel, s.guide ].filter(Boolean).join(' · ');
  /* COURSES CANNOT BE BOOKED. src/routes/dashboardBookingsCreate.js writes session_id only;
   it never writes course_run_id. The XOR CHECK constraint would accept it today, so this is
   a route change and not a migration. Until it lands the card is disabled WITH THE REASON
   ON SCREEN -- Law 14 forbids the silent version. */
  const noCourses = s.type === 'course';
  const hasCapacity = s.remaining != null && s.total != null;
  const leftCls = hasCapacity ? (full ? 'wim-none' : (s.remaining<=3 ? 'wim-low' : '')) : '';
  const leftTxt = hasCapacity ? (full ? 'Full — waitlist' : `${s.remaining} of ${s.total} spots`) : null;
  if(noCourses) return (
    <div key={s.id} className="wim-scard wim-course wim-blocked" aria-disabled="true">
      <div className="wim-spine"></div>
      <div className="wim-scard-in">
        <div className="wim-tok wim-tokx"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg></div>
        <div className="wim-sc-b">
          <div className="wim-sc-t">
            <span className="wim-sc-nm">{s.title}</span>
            <span className="wim-tag wim-course">{s.agency||'Course'}{s.level?' · '+s.level:''}</span>
            {s.diveLevel ? <span className={`wim-adlrs wim-${s.diveLevel}`}>{(diff(s.diveLevel) as { label: string }).label}</span> : null}
          </div>
          <div className="wim-sc-meta">{meta}</div>
          <div className="wim-blockwhy">Courses cannot be sold at the counter yet. The booking route writes a dive session and not a course run. Book this course from the Courses surface.</div>
        </div>
        <div className="wim-sc-r"><div className="wim-sc-amt">{money(s.priceMinor, s.currency || operator.currency)}</div></div>
      </div>
    </div>
  );
  return (
    <div key={s.id} className={`wim-scard ${on?'wim-on':''} ${on&&line&&line.waitlist?'wim-wait':''} ${full&&!on?'wim-fullx':''}`}
       onClick={() => toggle(s.id)} tabIndex={0} role="button" aria-pressed={on}
       onKeyDown={(event) => { if(event.key==='Enter'||event.key===' '){event.preventDefault();toggle(s.id)} }}>
      <div className="wim-spine"></div>
      <div className="wim-scard-in">
        <div className="wim-tok"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></div>
        <div className="wim-sc-b">
          <div className="wim-sc-t">
            <span className="wim-sc-nm">{s.title}</span>
            <span className="wim-tag wim-dive">{s.cat||'Dive'}</span>
            {s.diveLevel ? <span className={`wim-adlrs wim-${s.diveLevel}`}>{(diff(s.diveLevel) as { label: string }).label}</span> : null}
            {full ? <span className={`wim-tag ${on?'wim-wait':'wim-fullt'}`}>{on?'Waitlisted':'Full'}</span> : null}
          </div>
          <div className="wim-sc-meta">{meta}</div>
        </div>
        <div className="wim-sc-r">
          <div className="wim-sc-amt">{money(s.priceMinor, s.currency || operator.currency)}</div>
          {leftTxt ? <div className={`wim-sc-left ${leftCls}`}>{leftTxt}</div> : null}
        </div>
      </div>
    </div>
  );
}

function toggle(ref: string){
  const i = itinerary.findIndex(l=>l.ref===ref);
  if(i>-1){ setItinerary((current) => current.filter(l=>l.ref!==ref)); return; }
  const s = sessions.find(x=>x.id===ref); if(!s) return;
  setItinerary((current) => [...current, { uid:'u'+Math.random().toString(36).slice(2,8), ref:s.id, type:s.type, title:s.title,
                   date:s.date, time:s.time, priceMinor:s.priceMinor, currency:s.currency, diveLevel:s.diveLevel, maxDepth:s.maxDepth, guide:s.guide, pax:party, waitlist:s.remaining===0 }]);
}

function removeLine(uid: string){ setItinerary((current) => current.filter(l=>l.uid!==uid)); }

function renderOnWater(){
  const list = availOn(selDate);
  const p = selDate ? dparts(selDate) : null;
  const label = selDate && p ? (selDate===today ? 'Today' : `${p.wd} ${p.mon} ${p.dnum}`) : '';
  if(!list.length){
    return (
      <>
        <div className="wim-ow-h"><div className="wim-ow-t">Posted dives</div><div className="wim-ow-d">{label}</div></div>
        <div className="wim-ow-b"><div className="wim-ow-quiet">Nothing posted here yet. Posted dives will appear as soon as they are scheduled.</div></div>
      </>
    );
  }
  const capacityList = list.filter(x=>x.total != null && x.remaining != null);
  const seats = capacityList.reduce((a,x)=>a+x.total,0);
  const open  = capacityList.reduce((a,x)=>a+x.remaining,0);
  const from  = Math.min(...list.map(x=>x.priceMinor));
  const cheapest = list.find(x => x.priceMinor === from);
  const guides = Array.from(new Set(list.map(x=>x.guide).filter(Boolean) as string[]));
  const boats  = Array.from(new Set(list.map(x=>x.vessel).filter(Boolean) as string[]));
  return (
    <>
      <div className="wim-ow-h"><div className="wim-ow-t">Posted dives</div><div className="wim-ow-d">{label}</div></div>
      <div className="wim-ow-b">
        <div className="wim-ow-stats">
          <div className="wim-ow-s"><div className="wim-n">{list.length}</div><div className="wim-l">Run{list.length>1?'s':''}</div></div>
          {capacityList.length ? <div className="wim-ow-s"><div className={`wim-n ${open?'':'wim-none'}`}>{open}</div><div className="wim-l">{open?'Spots open':'Full'}</div></div> : null}
          <div className="wim-ow-s"><div className="wim-n wim-money">{(from/div(cheapest?.currency || operator.currency)).toFixed(0)}</div><div className="wim-l">From {cheapest?.currency || operator.currency}</div></div>
        </div>
        {guides.length ? <div className="wim-ow-g"><b>Guiding</b>{guides.join(', ')}</div> : null}
        {boats.length ? <div className="wim-ow-g"><b>Boats</b>{boats.join(', ')}</div> : null}
        {capacityList.length ? <div className="wim-ow-g"><b>Seats</b>{open} of {seats} still open across the day</div> : null}
      </div>
    </>
  );
}

function checkLevel(value: string, i: number){
  if(!value) return null;
  const held = lvl(value) as { short: string; depth: number; maxDiff: number };

  /* Owner ruling, 16 July: a mismatch is not a diver problem. It is a STAFFING problem.
     "yes you can dive, we're not gonna block you, but now I need 2 staff -- one who can
     keep you at the recreational limit and the other who's gonna take everybody down."
     So this does not say "brief the guide". It says the group splits and the boat needs
     a second divemaster. That is roster, payroll, and whether the trip runs. */
  const overCandidates = itinerary.filter(l=>l.type==='dive')
      .map(l=> l.diveLevel ? {t:l.title, d:l.maxDepth||0, D:diff(l.diveLevel) as { ord: number; v: string; label: string }, g:l.guide} : null)
      .filter((x): x is { t: string; d: number; D: { ord: number; v: string; label: string }; g: string | undefined } => !!x && x.D.ord > held.maxDiff);
  const over = overCandidates.sort((a,b)=>b.D.ord - a.D.ord)[0];

  if(!over) return null;
  return (
    <>
      <div className="wim-lh"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" width="11" height="11"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>This dive now needs a second guide</div>
      <b>{over.t} is rated <span className={`wim-adlrs wim-${over.D.v}`}>{over.D.label}</span> and reaches {over.d} m. This diver holds {held.short} — {held.depth} m.</b>{' '}
      The group splits: one guide holds the recreational limit, one takes the rest deep.{' '}
      <b>{over.g ? over.g+' cannot do both.' : 'One guide cannot do both.'} Check the roster before the boat loads, or sell the training instead.</b>{' '}
      The booking stands either way — nobody is refused.
    </>
  );
}

function setDiverField(i: number, field: string, value: string | boolean){
  if(i > 0 && ["first_name", "last_name", "preferred_name", "email", "phone"].includes(field)){
    setMatchedDivers((current) => {
      if(!current[i]) return current;
      const next = { ...current };
      delete next[i];
      return next;
    });
  }
  setManifestDrafts((current) => ({ ...current, [i]: { ...(current[i] || {}), [field]: value } }));
}

function setDiverSearchField(i: number, field: string, value: string){
  setDiverField(i, field, value);
  if(i <= 0) return;

  const draft = { ...(manifestDrafts[i] || {}), [field]: value };
  const nameQuery = [
    String(draft.first_name || "").trim(),
    String(draft.last_name || "").trim(),
  ].filter(Boolean).join(" ");
  const email = String(draft.email || "").trim();
  const phone = String(draft.phone || "").trim();
  const query = email || phone || nameQuery;

  setDiverLookupTerms((current) => ({ ...current, [i]: query }));
}

function applyDiverMatch(index: number, match: WalkInCustomer){
  setMatchedDivers((current) => ({ ...current, [index]: match }));
  setDiverLookupTerms((current) => ({ ...current, [index]: "" }));
  setDiverLookupResults((current) => ({ ...current, [index]: [] }));
  setManifestDrafts((current) => ({
    ...current,
    [index]: {
      ...(current[index] || {}),
      first_name: match.first_name || "",
      last_name: match.last_name || "",
      preferred_name: match.preferred_name || "",
      dob: match.dob || "",
      nationality: match.nationality || "",
      email: match.email || "",
      phone: match.phone || "",
      gender: match.gender || "",
      id_type: match.id_type || "passport",
      id_number: match.id_number || "",
      emergency_contact_name:
        match.emergency_contact_name || "",
      emergency_contact_relationship:
        match.emergency_contact_relationship || "",
      emergency_contact_phone:
        match.emergency_contact_phone || "",
      emergency_contact_phone_secondary:
        match.emergency_contact_phone_secondary || "",
      guardian_name: match.guardian_name || "",
      guardian_relationship:
        match.guardian_relationship || "",
      guardian_phone: match.guardian_phone || "",
      guardian_id_document_type:
        match.guardian_id_document_type || "passport",
      guardian_id_document_number:
        match.guardian_id_document_number || "",
      guardian_nationality_code:
        match.guardian_nationality_code || "",
    },
  }));
}

useEffect(() => {
  const entries = Object.entries(diverLookupTerms)
    .map(([index, term]) => ({ index: Number(index), term: String(term || "").trim() }))
    .filter((item) => item.index > 0 && item.term.length >= 3 && !matchedDivers[item.index]);

  if(!entries.length) return;

  let cancelled = false;
  const timer = window.setTimeout(async () => {
    for(const item of entries){
      try {
        const response = await apiFetch(`/api/v1/dashboard/customers/search?q=${encodeURIComponent(item.term)}`);
        const data = await response.json();
        const results = Array.isArray(data?.data?.results)
          ? data.data.results.map(mapLookupCustomerToWalkInCustomer)
          : [];
        if(cancelled) return;
        setDiverLookupResults((current) => ({ ...current, [item.index]: results }));
      } catch {
        if(cancelled) return;
        setDiverLookupResults((current) => ({ ...current, [item.index]: [] }));
      }
    }
  }, 350);

  return () => {
    cancelled = true;
    window.clearTimeout(timer);
  };
}, [diverLookupTerms, matchedDivers]);

function diverField(i: number, field: string): string {
  const draft = manifestDrafts[i];
  if(draft && draft[field] !== undefined) return String(draft[field]);
  if(i === 0 && customer){
    const fromCustomer: Record<string, string | undefined> = {
      first_name: customer.first_name,
      last_name: customer.last_name,
      preferred_name: customer.preferred_name,
      dob: customer.dob,
      nationality: customer.nationality,
      email: customer.email,
      phone: customer.phone,
      gender: customer.gender,
      id_type: customer.id_type,
      id_number: customer.id_number,
      emergency_contact_name:
        customer.emergency_contact_name,
      emergency_contact_relationship:
        customer.emergency_contact_relationship,
      emergency_contact_phone:
        customer.emergency_contact_phone,
      emergency_contact_phone_secondary:
        customer.emergency_contact_phone_secondary,
      guardian_name: customer.guardian_name,
      guardian_relationship:
        customer.guardian_relationship,
      guardian_phone: customer.guardian_phone,
      guardian_id_document_type:
        customer.guardian_id_document_type,
      guardian_id_document_number:
        customer.guardian_id_document_number,
      guardian_nationality_code:
        customer.guardian_nationality_code,
    };
    return fromCustomer[field] || "";
  }
  return "";
}

function transactionIdentity(){
  return {
    firstName: diverField(0, "first_name").trim(),
    lastName: diverField(0, "last_name").trim(),
    preferredName: diverField(0, "preferred_name").trim(),
    email: diverField(0, "email").trim(),
    phone: diverField(0, "phone").trim(),
    nationality: diverField(0, "nationality").trim(),
  };
}

function transactionIdentityError(identity = transactionIdentity()): string {
  if(!identity.firstName) return "Enter Diver 1's legal first name.";
  if(!identity.lastName) return "Enter Diver 1's legal last name.";
  if(!identity.email && !identity.phone) return "The customer needs a legal first name, last name, and either an email or phone.";
  return "";
}

function manifestReadinessError(): string {
  if(!itinerary.length) return "";

  for(let i = 0; i < party; i += 1){
    const label = `Diver ${i + 1}`;
    const requiredFields: Array<[string, string]> = [
      ["first_name", "legal first name"],
      ["last_name", "legal last name"],
      ["dob", "date of birth"],
      ["nationality", "nationality"],
      ["gender", "gender"],
      ["id_number", "identity document number"],
      ["accommodation", "accommodation"],
      ["room_number", "room number"],
      ["emergency_contact_name", "emergency contact name"],
      ["emergency_contact_relationship", "emergency contact relationship"],
      ["emergency_contact_phone", "emergency contact phone"],
    ];

    for(const [field, fieldLabel] of requiredFields){
      if(!diverField(i, field).trim()){
        return `${label}: enter ${fieldLabel}.`;
      }
    }

    const documentType =
      diverField(i, "id_type").trim() || "passport";

    if(!["passport", "national_id"].includes(documentType)){
      return `${label}: select a valid identity document type.`;
    }

    if(!identityReviewed[i]){
      return `${label}: review and confirm the reusable identity details.`;
    }

    const minorStatus = activityDateMinorStatus(i);

    if(minorStatus.state === "pending"){
      return `${label}: enter a valid date of birth for the selected activity dates.`;
    }

    if(minorStatus.state === "minor"){
      const guardianFields: Array<[string, string]> = [
        ["guardian_name", "guardian legal name"],
        ["guardian_relationship", "guardian relationship"],
        ["guardian_phone", "guardian phone"],
        ["guardian_id_document_number", "guardian document number"],
        ["guardian_nationality_code", "guardian nationality"],
      ];

      for(const [field, fieldLabel] of guardianFields){
        if(!diverField(i, field).trim()){
          return `${label}: enter ${fieldLabel}.`;
        }
      }

      const guardianDocumentType =
        diverField(i, "guardian_id_document_type").trim()
        || "passport";

      if(
        !["passport", "national_id"].includes(
          guardianDocumentType
        )
      ){
        return `${label}: select a valid guardian document type.`;
      }
    }
  }

  return "";
}

function setMed(i: number, kind: "clear" | "warn" | "stop"){
  setMedicalStates((current) => ({ ...current, [i]: kind }));
}

function selPay(m: string){ setPayMethod(m); }

function applyPromo(){
  const v = (promoInput||'').trim().toUpperCase();
  setPromo(v==='REEF10' ? {label:'REEF10',kind:'pct',value:10} : (v?{label:v+' — not recognised',kind:'pct',value:0}:null));
}

function renderPayment(){
  const pm = (k: string,n: string,s: string,disabled=false) => (
    <button className={`wim-pm ${payMethod===k?'wim-on':''}`} onClick={() => selPay(k)} disabled={disabled} style={disabled ? {opacity:.52,cursor:"not-allowed"} : undefined}>
      <div className="wim-pmr"></div><div><div className="wim-pm-n">{n}</div><div className="wim-pm-s">{s}</div></div>
    </button>
  );
  return (
    <>
      <div className="wim-paygrid">
        {pm('stripe','Send a Payment','Not available yet',true)}
        {pm('cash','Cash at Register','Records the cash settlement observed at the register')}
        {pm('card_terminal','Card on Terminal',"Records settlement on the operator's own terminal")}
        {pm('hold','Hold','Creates a time-sensitive hold; no settlement is recorded')}
      </div>
      <div className="wim-promo">
        <input className="wim-inp" id="promoIn" placeholder="Promo code — try REEF10" value={promoInput} onChange={(event) => setPromoInput(event.target.value)} />
        <button className="wim-btn" onClick={applyPromo}>Apply</button>
      </div>
      <div className="wim-invnote">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h4"/></svg>
        <span>AQUORIX generates an invoice on payment — a stored billing record and a PDF — carrying your identity, the customer&rsquo;s legal name, the itinerary, discounts, total, method, and status. Filed under Billing and attached to the confirmation.</span>
      </div>
    </>
  );
}

/* ============ STEP 5 ============ */
/* CHANNEL TRUTH, read from src/services/notifications.js on 16 July.
   sendConfirmation() defaults to channels:["email"] and sends the guest an email
   via Resend. There is NO guest WhatsApp function: sendOperatorWhatsApp() hardcodes
   its recipient to env VIKING_TEST_WHATSAPP_TO, and the production WhatsApp Business
   account is an open go-live item. The self-completion link has a spine
   (booking_participants.unique_token, forms_sent_at, form_submissions) but the
   notification service does not send it.
   Law 14: both are shown, dark and honest. Neither is silently dropped. */
function renderConfirm(){
  const transaction = transactionIdentity();
  const hasPhone = !!transaction.phone;
  const hasEmail = !!transaction.email;
  const payLine = payMethod==='hold'
    ? 'recorded as a time-sensitive hold; no settlement is recorded.'
    : payMethod==='cash'
      ? 'cash at the register is recorded settled after the booking is created.'
      : payMethod==='card_terminal'
        ? 'the operator-terminal card settlement is recorded after the booking is created.'
        : 'the clerk must choose the settlement state before creating the booking.';
  let channels: React.ReactNode;
  if(!hasEmail && !hasPhone){
    channels = <div className="wim-chan"><div className="wim-cs">Enter either an email or a phone for Diver 1 before creating the booking.</div></div>;
  } else {
    channels = (
      <>
        {hasEmail ? <div className="wim-chan"><div className="wim-ci wim-em">@</div><div><div className="wim-cn">Email</div><div className="wim-cs">{transaction.email}</div></div></div> : null}
        {hasPhone ? <div className="wim-chan wim-dark"><div className="wim-ci wim-wa">W</div><div><div className="wim-cn">Phone</div><div className="wim-cs">{transaction.phone} · no WhatsApp delivery is claimed.</div></div><div className="wim-cd wim-pend">Recorded</div></div> : null}
      </>
    );
  }
  return (
    <>
      {channels}
      <div className="wim-carry">
        <b>Booking</b> is created through the live booking route.
        &nbsp;<b>Payment:</b> {payLine}
        &nbsp;<b>Notifications and documents</b> are reported only when the backend returns an outcome.
      </div>
    </>
  );
}

function activityDateMinorStatus(i: number){
  const dob = diverField(i, "dob").trim();
  const activityDates = Array.from(
    new Set(itinerary.map((item) => item.date).filter(Boolean))
  ).sort();

  if(!dob || !activityDates.length){
    return {
      state: "pending" as const,
      activityDate: "",
      age: null as number | null,
    };
  }

  const dobParts = dob.split("-").map(Number);
  if(
    dobParts.length !== 3
    || dobParts.some((part) => !Number.isInteger(part))
  ){
    return {
      state: "pending" as const,
      activityDate: "",
      age: null as number | null,
    };
  }

  const [birthYear, birthMonth, birthDay] = dobParts;
  const evaluated = activityDates.map((activityDate) => {
    const [year, month, day] = activityDate.split("-").map(Number);
    let age = year - birthYear;

    if(
      month < birthMonth
      || (month === birthMonth && day < birthDay)
    ){
      age -= 1;
    }

    return { activityDate, age };
  });

  const invalid = evaluated.find(
    (item) => !Number.isInteger(item.age) || item.age < 0
  );

  if(invalid){
    return {
      state: "pending" as const,
      activityDate: invalid.activityDate,
      age: null as number | null,
    };
  }

  const minor = evaluated.find((item) => item.age < 18);
  if(minor){
    return {
      state: "minor" as const,
      activityDate: minor.activityDate,
      age: minor.age,
    };
  }

  const youngest = evaluated.reduce((current, item) =>
    item.age < current.age ? item : current
  );

  return {
    state: "adult" as const,
    activityDate: youngest.activityDate,
    age: youngest.age,
  };
}

function renderManifest(){
  const pax = itinerary.length ? party : 0;
  if(!pax){ return <div className="wim-emptyday">Add a dive or a course and the divers appear here.</div>; }
  return (
    <>
      {Array.from({length:pax},(_,i)=>{
        const matchedDiver = i === 0 ? customer : matchedDivers[i];
        const me = !!matchedDiver;
        const med = medicalStates[i] || "clear";
        const minorStatus = activityDateMinorStatus(i);
        return (
          <div className="wim-dcard" key={i}>
            <div className="wim-dcard-h">
              <div className="wim-dcard-n">{i+1}</div>
              <div className="wim-dcard-t">{i === 0 ? rosterName(customer,i) : (matchedDiver ? rosterName(matchedDiver,0) : `Diver ${i+1}`)}</div>
              {me ? (
                <span className="wim-tag wim-dive">Returning diver - review details</span>
              ) : (
                <span className="wim-tag wim-dive">New diver - review details</span>
              )}
            </div>
            <div className="wim-dcard-b">
              <div className="wim-row3">
                <div className="wim-fgrp"><label className="wim-fl">Legal first name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Jonathan" value={diverField(i,"first_name")} onChange={(e) => setDiverSearchField(i,"first_name",e.target.value)} /></div>
                <div className="wim-fgrp"><label className="wim-fl">Legal last name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Robinson" value={diverField(i,"last_name")} onChange={(e) => setDiverSearchField(i,"last_name",e.target.value)} /></div>
                <div className="wim-fgrp"><label className="wim-fl">Preferred name</label><input className="wim-inp" placeholder="John" value={diverField(i,"preferred_name")} onChange={(e) => setDiverField(i,"preferred_name",e.target.value)} /></div>
              </div>
              {i > 0 && diverLookupResults[i]?.length ? (
                <div>
                  {diverLookupResults[i].map((match) => {
                    const matchName = `${match.preferred_name||match.first_name} ${match.last_name||""}`.trim();
                    const matchInitials = ((match.preferred_name||match.first_name||"?")[0] + (match.last_name||" ")[0]).toUpperCase();
                    const matchMeta = [match.phone, match.email, match.last_booking_date ? `last booking: ${match.last_booking_date}` : null].filter(Boolean).join(" · ");
                    return (
                      <div className="wim-match" key={match.guest_id || match.email || matchName}>
                        <div className="wim-av">{matchInitials}</div>
                        <div className="wim-match-b">
                          <div className="wim-st">Returning diver match</div>
                          <div className="wim-nm">{matchName}</div>
                          <div className="wim-mt">{matchMeta}</div>
                        </div>
                        <button className="wim-btn wim-primary" onClick={() => applyDiverMatch(i, match)}>Use this diver</button>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="wim-row3">
                <div className="wim-fgrp"><label className="wim-fl">Date of birth <span className="wim-req">* REQ</span></label><input className="wim-inp" type="date" value={diverField(i,"dob")} onChange={(e) => setDiverField(i,"dob",e.target.value)} /></div>
                <div className="wim-fgrp"><label className="wim-fl">Nationality <span className="wim-req">* REQ</span></label>
                  <select className="wim-inp" value={diverField(i,"nationality")} onChange={(e) => setDiverField(i,"nationality",e.target.value)}>
                    <option value="">Select</option>
                    {nationalities.map((x) => <option key={x.c} value={x.c}>{x.n}</option>)}
                  </select></div>
                <div className="wim-fgrp"><label className="wim-fl">Gender <span className="wim-req">* REQ</span></label>
                  <select className="wim-inp" value={diverField(i,"gender")} onChange={(e) => setDiverField(i,"gender",e.target.value)}>
                    <option value="">Select</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select></div>
              </div>
              <div className={`wim-age-status wim-age-${minorStatus.state}`}>
                {minorStatus.state === "minor" ? (
                  <><b>Minor on activity date.</b> Age {minorStatus.age} on {minorStatus.activityDate}. Guardian identity is required.</>
                ) : minorStatus.state === "adult" ? (
                  <><b>Adult on selected activity dates.</b> Age {minorStatus.age} on {minorStatus.activityDate}. Guardian identity is not required.</>
                ) : (
                  <><b>Minor status pending.</b> Enter a valid date of birth and select an activity.</>
                )}
              </div>
              <div className="wim-pairwrap">
                <div className="wim-row2">
                  <div className="wim-fgrp" style={{margin:0}}><label className="wim-fl">Certification agency</label>
                    <select className="wim-inp" value={diverField(i,"agency")} onChange={(e) => setDiverField(i,"agency",e.target.value)}><option value="">Agency</option><option value="PADI">PADI</option><option value="SSI">SSI</option><option value="NAUI">NAUI</option><option value="SDI / TDI">SDI / TDI</option><option value="RAID">RAID</option><option value="BSAC">BSAC</option><option value="CMAS">CMAS</option><option value="Other">Other</option></select></div>
                  <div className="wim-fgrp" style={{margin:0}}><label className="wim-fl">Level <span className="wim-req">* REQ</span></label>
                    <select className="wim-inp" onChange={(event) => setManifestLevels((current) => ({ ...current, [i]: event.target.value }))}><option value="">What can this diver dive?</option>{LEVELS.map(l=><option key={l.v} value={l.v}>{l.label}</option>)}</select></div>
                </div>
                <p className="wim-pairnote"><b>The agency does not matter. The level does.</b> Every agency teaches to the same depth ladder — the card says PADI or SSI, the boat only needs to know how deep this diver may go.</p>
                <div className="wim-lvlnote" id={`lvl${i}`}>{checkLevel(manifestLevels[i] || "", i)}</div>
              </div>
              <div className="wim-pairwrap">
                <div className="wim-row2">
                  <div className="wim-fgrp" style={{margin:0}}><label className="wim-fl">Email <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="name@email.com" value={diverField(i,"email")} onChange={(e) => setDiverSearchField(i,"email",e.target.value)} /></div>
                  <div className="wim-fgrp" style={{margin:0}}><label className="wim-fl">Phone <span className="wim-opt">encouraged</span></label><input className="wim-inp" placeholder="+962 79 000 0000" value={diverField(i,"phone")} onChange={(e) => setDiverSearchField(i,"phone",e.target.value)} /></div>
                </div>
                <p className="wim-pairnote"><b>One is required. Both is better.</b> Email and phone are each unique in AQUORIX — either one finds this diver next time, and two make them findable twice.</p>
              </div>
              <div className="wim-contract-section">
                <div className="wim-contract-heading">Diver identity documentation</div>
                <div className="wim-row2">
                  <div className="wim-fgrp"><label className="wim-fl">Document type <span className="wim-req">* REQ</span></label>
                    <select className="wim-inp" value={diverField(i,"id_type") || "passport"} onChange={(e) => setDiverField(i,"id_type",e.target.value)}><option value="passport">Passport</option><option value="national_id">National ID</option></select></div>
                  <div className="wim-fgrp"><label className="wim-fl">Document number <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Enter the official document number" value={diverField(i,"id_number")} onChange={(e) => setDiverField(i,"id_number",e.target.value)} /></div>
                </div>
                <label className="wim-chk">
                  <input
                    type="checkbox"
                    checked={!!identityReviewed[i]}
                    onChange={(event) => setIdentityReviewed((current) => ({
                      ...current,
                      [i]: event.target.checked,
                    }))}
                  />
                  I reviewed these identity details with the customer
                </label>
              </div>

              <div className="wim-contract-section">
                <div className="wim-contract-heading">Stay details - this booking only</div>
                <div className="wim-row2">
                  <div className="wim-fgrp"><label className="wim-fl">Accommodation <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Hotel, resort, boat, or private stay" value={diverField(i,"accommodation")} onChange={(e) => setDiverField(i,"accommodation",e.target.value)} /></div>
                  <div className="wim-fgrp"><label className="wim-fl">Room number <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Room or cabin number" value={diverField(i,"room_number")} onChange={(e) => setDiverField(i,"room_number",e.target.value)} /></div>
                </div>
                <p className="wim-pairnote"><b>Fresh for every booking.</b> Previous accommodation and room details are never reused.</p>
              </div>

              <div className="wim-contract-section">
                <div className="wim-contract-heading">Emergency contact</div>
                <div className="wim-row2">
                  <div className="wim-fgrp"><label className="wim-fl">Contact name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Full legal name" value={diverField(i,"emergency_contact_name")} onChange={(e) => setDiverField(i,"emergency_contact_name",e.target.value)} /></div>
                  <div className="wim-fgrp"><label className="wim-fl">Relationship <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Parent, spouse, partner, friend" value={diverField(i,"emergency_contact_relationship")} onChange={(e) => setDiverField(i,"emergency_contact_relationship",e.target.value)} /></div>
                </div>
                <div className="wim-row2">
                  <div className="wim-fgrp"><label className="wim-fl">Primary phone <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="+962 79 000 0000" value={diverField(i,"emergency_contact_phone")} onChange={(e) => setDiverField(i,"emergency_contact_phone",e.target.value)} /></div>
                  <div className="wim-fgrp"><label className="wim-fl">Secondary phone <span className="wim-opt">optional</span></label><input className="wim-inp" placeholder="Additional contact number" value={diverField(i,"emergency_contact_phone_secondary")} onChange={(e) => setDiverField(i,"emergency_contact_phone_secondary",e.target.value)} /></div>
                </div>
              </div>

              {minorStatus.state === "minor" ? (
                <div className="wim-contract-section wim-guardian-section">
                  <div className="wim-contract-heading">Guardian identity</div>
                  <div className="wim-row3">
                    <div className="wim-fgrp"><label className="wim-fl">Guardian name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Full legal name" value={diverField(i,"guardian_name")} onChange={(e) => setDiverField(i,"guardian_name",e.target.value)} /></div>
                    <div className="wim-fgrp"><label className="wim-fl">Relationship <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Parent or legal guardian" value={diverField(i,"guardian_relationship")} onChange={(e) => setDiverField(i,"guardian_relationship",e.target.value)} /></div>
                    <div className="wim-fgrp"><label className="wim-fl">Guardian phone <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="+962 79 000 0000" value={diverField(i,"guardian_phone")} onChange={(e) => setDiverField(i,"guardian_phone",e.target.value)} /></div>
                  </div>
                  <div className="wim-row3">
                    <div className="wim-fgrp"><label className="wim-fl">Document type <span className="wim-req">* REQ</span></label>
                      <select className="wim-inp" value={diverField(i,"guardian_id_document_type") || "passport"} onChange={(e) => setDiverField(i,"guardian_id_document_type",e.target.value)}><option value="passport">Passport</option><option value="national_id">National ID</option></select></div>
                    <div className="wim-fgrp"><label className="wim-fl">Document number <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Guardian document number" value={diverField(i,"guardian_id_document_number")} onChange={(e) => setDiverField(i,"guardian_id_document_number",e.target.value)} /></div>
                    <div className="wim-fgrp"><label className="wim-fl">Nationality <span className="wim-req">* REQ</span></label>
                      <select className="wim-inp" value={diverField(i,"guardian_nationality_code")} onChange={(e) => setDiverField(i,"guardian_nationality_code",e.target.value)}>
                        <option value="">Select</option>
                        {nationalities.map((x) => <option key={x.c} value={x.c}>{x.n}</option>)}
                      </select></div>
                  </div>
                </div>
              ) : null}
              <div className="wim-fgrp">
                <label className="wim-fl">Diver Medical — status only <span className="wim-req">* REQ</span></label>
                <div className="wim-medrow">
                  <button className={`wim-medbtn ${med==="clear"?"wim-on":""}`} onClick={() => setMed(i,"clear")}>No conditions declared</button>
                  <button className={`wim-medbtn ${med==="warn"?"wim-on wim-warn":""}`} onClick={() => setMed(i,"warn")}>Declared — cleared by physician</button>
                  <button className={`wim-medbtn ${med==="stop"?"wim-on wim-stop":""}`} onClick={() => setMed(i,"stop")}>Declared — awaiting clearance</button>
                </div>
                <div className="wim-medflag" id={`mf${i}`} style={{display: med==="stop" ? "block" : "none"}}>Pending physician clearance blocks diving until cleared. The booking still stands. The full Diver Medical arrives on the self-completion link.</div>
              </div>
              <div className="wim-chkrow">
                <label className="wim-chk"><input type="checkbox" checked={!!manifestDrafts[i]?.nitrox} onChange={(e) => setDiverField(i,"nitrox",e.target.checked)} /> Nitrox</label>
                <label className="wim-chk"><input type="checkbox" checked={!!manifestDrafts[i]?.rental_gear} onChange={(e) => setDiverField(i,"rental_gear",e.target.checked)} /> Rental gear</label>
              </div>
            </div>
          </div>
        );
      })}
      <p className="wim-reqnote"><b>* REQ = required.</b> <b>Required before booking:</b> reviewed reusable identity, contact information, accommodation, room number, and emergency contact. <b>When the diver is a minor on an activity date, guardian identity is also required.</b><br/><b>Dive readiness:</b> certification level and medical status remain operational requirements. The agency, physical certification card, secondary emergency phone, and full medical form may be completed separately. A certification level below the planned dive changes the staffing and dive plan; it does not erase the booking.</p>
    </>
  );
}

function renderRail(){
  const t = totals();
  const pax = itinerary.reduce((a,l)=>a+l.pax,0);
  const railParty = itinerary.length
    ? `${itinerary.length} line${itinerary.length>1?'s':''} · ${pax} diver spot${pax>1?'s':''}`
    : 'Nothing added yet';
  const days = Array.from(new Set(itinerary.map(l=>l.date))).sort();
  const identityError =
    transactionIdentityError() || manifestReadinessError();
  const ready =
    itinerary.length > 0 && !identityError && !!payMethod;
  const closeNote = !itinerary.length
    ? 'Pick a day and add a dive.'
    : identityError
      ? identityError
      : !payMethod
        ? 'Choose the settlement state in Payment.'
        : payMethod==='hold'
          ? 'Creates a time-sensitive hold. No settlement is recorded.'
          : payMethod==='cash'
            ? 'Records the cash settlement observed at the register.'
            : "Records settlement on the operator's own terminal.";

  return (
    <aside className="wim-rail" id="rail">
      <div className="wim-rail-h">
        <div className="wim-rail-t">The adventure</div>
        <div className="wim-rail-p" id="railParty">{railParty}</div>
      </div>
      <div className="wim-rail-body" id="railBody">
        {!itinerary.length ? (
          <div className="wim-rail-empty">Pick a day, then tap any dive or course.<br/>The name can come after.</div>
        ) : (
          days.map(d=>{
            const p = dparts(d);
            const lines = itinerary.filter(l=>l.date===d).sort((a,b)=>a.time.localeCompare(b.time));
            return (
              <div key={d}>
                <div className="wim-rail-day">{p.wd} {p.mon} {p.dnum}</div>
                {lines.map(l=>(
                  <div key={l.uid} className={`wim-rl ${l.type==='course'?'wim-course':''}`}>
                    <div className="wim-rl-sp"></div>
                    <div className="wim-rl-b">
                      <div className="wim-rl-n">{l.title}</div>
                      <div className="wim-rl-m">{t12(l.time)} · {l.pax} diver{l.pax>1?'s':''}{l.waitlist?' · waitlist':''}</div>
                    </div>
                    <div className="wim-rl-r">
                      <div className={`wim-rl-a ${l.waitlist?'wim-free':''}`}>{l.waitlist?'No charge':money(l.priceMinor*l.pax, l.currency || operator.currency)}</div>
                      {!l.waitlist ? (
                        <div className="wim-stepper"><button onClick={() => setLinePax(l.uid,-1)}>&minus;</button><span>{l.pax}</span><button onClick={() => setLinePax(l.uid,1)}>+</button></div>
                      ) : null}
                      <button className="wim-rl-x" onClick={() => removeLine(l.uid)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>
      <div className="wim-rail-tot" id="railTot">
        <div className="wim-trow"><span>Subtotal</span><span className="wim-tv">{money(t.subtotal, operator.currency)}</span></div>
        {t.promoAmt ? <div className="wim-trow wim-disc"><span>Promo {promo?.label}</span><span className="wim-tv">&minus;{money(t.promoAmt, operator.currency)}</span></div> : null}
        <div className="wim-trow wim-grand"><span>Total</span><span className="wim-tv">{money(t.grand, operator.currency)}</span></div>
      </div>
      <div className="wim-rail-foot">
        <button className="wim-btn wim-primary" id="closeBtn" onClick={createNotify} disabled={!ready || creating || createdBookingId !== null}>Create and notify</button>
        {settlementFailure ? (
          <>
            <div className="wim-rail-note" style={{color:"var(--error)"}}>
              Booking BCD-{settlementFailure.bookingId} was created. {settlementFailure.message} Only its settlement record needs re-recording. Do not create this booking again.
            </div>
            <a className="wim-refbtn" href={`/dashboard/bookings/${encodeURIComponent(String(settlementFailure.bookingId))}`}>
              Open BCD-{settlementFailure.bookingId}
            </a>
          </>
        ) : createErr ? <div className="wim-rail-note" style={{color:"var(--error)"}}>{createErr}</div> : null}
        <div className="wim-rail-note" id="closeNote">{closeNote}</div>
      </div>
    </aside>
  );
}

function totals(){
  const subtotal = itinerary.filter(l=>!l.waitlist).reduce((a,l)=>a+l.priceMinor*l.pax,0);
  let promoAmt = 0;
  if(promo && promo.value) promoAmt = promo.kind==='pct' ? Math.round(subtotal*promo.value/100) : promo.value;
  return { subtotal, promoAmt, grand: Math.max(0, subtotal - promoAmt) };
}

  async function doLookup(){
    const q = lookup.trim()
    if(!q) return
    const response = await apiFetch(`/api/v1/dashboard/customers/search?q=${encodeURIComponent(q)}`)
    const data = await response.json()
    const results = Array.isArray(data?.data?.results) ? data.data.results : []
    setLookupResults(results.map(mapLookupCustomerToWalkInCustomer))
    setStep1Mode("match")
  }

  function pickReturning(match: WalkInCustomer){
    setCustomer({...match})
    setManifestDrafts((current) => ({
      ...current,
      0: {
        ...(current[0] || {}),
        first_name: match.first_name || "",
        last_name: match.last_name || "",
        preferred_name: match.preferred_name || "",
        dob: match.dob || "",
        nationality: match.nationality || "",
        email: match.email || "",
        phone: match.phone || "",
        gender: match.gender || "",
        id_type: match.id_type || "passport",
        id_number: match.id_number || "",
        emergency_contact_name:
          match.emergency_contact_name || "",
        emergency_contact_relationship:
          match.emergency_contact_relationship || "",
        emergency_contact_phone:
          match.emergency_contact_phone || "",
        emergency_contact_phone_secondary:
          match.emergency_contact_phone_secondary || "",
        guardian_name: match.guardian_name || "",
        guardian_relationship:
          match.guardian_relationship || "",
        guardian_phone: match.guardian_phone || "",
        guardian_id_document_type:
          match.guardian_id_document_type || "passport",
        guardian_id_document_number:
          match.guardian_id_document_number || "",
        guardian_nationality_code:
          match.guardian_nationality_code || "",
      },
    }))
    setStep1Mode("chip")
  }

  function newCustomer(){
    setStep1Mode("capture")
  }

  function saveNew(){
    const g = (id: keyof typeof newCustomerDraft) => (newCustomerDraft[id]||"").trim()
    setCustomer({ guest_id:null, first_name:g('fn')||'New', last_name:g('ln')||'Diver', preferred_name:g('pn'),
                 dob:g('dob'), nationality:g('nat'), email:g('em'), phone:g('ph'),
                 gender:g('gen'), id_type:g('idt')||'passport', id_number:g('idn'), isNew:true })
    setStep1Mode("chip")
  }

  function resetStep1(){
    setCustomer(null)
    setLookup("")
    setLookupResults([])
    setStep1Mode("search")
  }

  async function createNotify(){
  if(createdBookingId !== null) return;
  if(itinerary.some((l) => l.pax < party)){
    setCreateErr("Per-line diver counts are not bookable yet. Every diver is booked on every selected dive. Set each dive to the full group to book now.");
    return;
  }
  const transaction = transactionIdentity();
  const identityError =
    transactionIdentityError(transaction)
    || manifestReadinessError();

  if(identityError){
    setCreateErr(identityError);
    return;
  }
  setCreateErr("");
  setSettlementFailure(null);

  setCreating(true);
  const sessionIds = itinerary.map((l) => Number(l.ref.replace("dive_session:", "")));
  const divers = Array.from({length: party}, (_, i) => ({
    first_name: diverField(i, "first_name"),
    last_name: diverField(i, "last_name"),
    preferred_name: diverField(i, "preferred_name"),
    email: diverField(i, "email"),
    phone_e164: diverField(i, "phone"),
    date_of_birth: diverField(i, "dob"),
    nationality_code: diverField(i, "nationality"),
    gender: diverField(i, "gender"),
    id_document_type:
      diverField(i, "id_type") || "passport",
    id_document_number: diverField(i, "id_number"),
    accommodation: diverField(i, "accommodation"),
    room_number: diverField(i, "room_number"),
    emergency_contact_name:
      diverField(i, "emergency_contact_name"),
    emergency_contact_relationship:
      diverField(i, "emergency_contact_relationship"),
    emergency_contact_phone:
      diverField(i, "emergency_contact_phone"),
    emergency_contact_phone_secondary:
      diverField(i, "emergency_contact_phone_secondary"),
    guardian_name: diverField(i, "guardian_name"),
    guardian_relationship:
      diverField(i, "guardian_relationship"),
    guardian_phone: diverField(i, "guardian_phone"),
    guardian_id_document_type:
      diverField(i, "guardian_id_document_type")
      || "passport",
    guardian_id_document_number:
      diverField(i, "guardian_id_document_number"),
    guardian_nationality_code:
      diverField(i, "guardian_nationality_code"),
    lookup_user_id: i === 0 && customer?.guest_id ? customer.guest_id : (matchedDivers[i]?.guest_id || null),
    request_nitrox: !!manifestDrafts[i]?.nitrox,
    needs_rental_gear: !!manifestDrafts[i]?.rental_gear,
  }));
  let bookingId: number | string | null = null;
  let createResult: WalkInCreateResult | null = null;
  let settlementResult: WalkInSettlementResult | null = null;
  try {
    try {
      const response = await apiFetch("/api/v1/dashboard/bookings/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_ids: sessionIds,
          payment_method: payMethod,
          customer: {
            first_name: transaction.firstName,
            last_name: transaction.lastName,
            email: transaction.email,
            phone_e164: transaction.phone,
          },
          divers,
        }),
      });
      const data = await response.json();
      if(!response.ok || !data.ok){
        const codeMessages: Record<string, string> = {
          SESSION_FULL: "One of the selected dives is full. Remove it or reduce the group.",
          SESSION_CANCELLED: "One of the selected dives has been cancelled.",
          SESSION_NOT_FOUND: "One of the selected dives could not be found. Refresh and try again.",
          SESSION_CURRENCY_MISMATCH: "The selected dives use different currencies and cannot be booked together.",
          SESSION_PRICE_MISSING: "One of the selected dives is not priced.",
          CUSTOMER_IDENTITY_REQUIRED: "The customer needs a legal first name, last name, and either an email or phone.",
          DIVERS_REQUIRED: "Add at least one diver to the manifest.",
          PARTICIPANT_IDENTITY_INCOMPLETE: "Complete the required participant identity, emergency-contact, and guardian information before continuing.",
        };

        const fieldErrorMessages = Object.values(
          data?.field_errors || {}
        ).filter((value) => typeof value === "string");

        const readinessMessage =
          data?.code === "PARTICIPANT_IDENTITY_INCOMPLETE"
          && fieldErrorMessages.length
            ? String(fieldErrorMessages[0])
            : "";

        setCreateErr(
          readinessMessage
          || codeMessages[data?.code]
          || data?.error
          || "The booking could not be created. Please try again."
        );
        return;
      }
      bookingId = data.bookingId;
      if(bookingId === null || bookingId === undefined){
        setCreateErr("The booking response did not include a booking reference. Please check Bookings before trying again.");
        return;
      }
      setCreatedBookingId(bookingId);
      createResult = {
        bookingId,
        bookingStatus: String(data.bookingStatus || ""),
        paymentStatus: String(data.paymentStatus || ""),
        paymentMethod: String(data.paymentMethod || ""),
        source: String(data.source || ""),
        holdExpiresAt: data.holdExpiresAt ? String(data.holdExpiresAt) : null,
      };
    } catch (err) {
      setCreateErr("Something went wrong reaching the server. The booking was not created.");
      return;
    }

    if(payMethod === "cash" || payMethod === "card_terminal"){
      try {
        const paymentResponse = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(String(bookingId))}/record-payment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ payment_method: payMethod }),
        });
        const paymentData = await paymentResponse.json();
        if(!paymentResponse.ok || !paymentData.ok){
          setSettlementFailure({
            bookingId,
            message: paymentData?.message || paymentData?.error || "Its settlement state could not be recorded.",
          });
          return;
        }
        settlementResult = {
          bookingStatus: String(paymentData?.booking?.booking_status || ""),
          paymentStatus: String(paymentData?.booking?.payment_status || ""),
          paymentMethod: String(paymentData?.booking?.payment_method || ""),
          paidAt: paymentData?.booking?.paid_at ? String(paymentData.booking.paid_at) : null,
        };
      } catch (err) {
        setSettlementFailure({
          bookingId,
          message: "The settlement service could not be reached.",
        });
        return;
      }
    }
  } finally {
    setCreating(false);
  }
  if(bookingId === null || createResult === null) return;

  const t = totals();
  const nm = transaction.preferredName || transaction.firstName;
  const full = `${transaction.firstName} ${transaction.lastName}`.trim();
  const ref = "BCD-" + bookingId;
  const days = Array.from(new Set(itinerary.map(l=>l.date))).sort();
  const pax = itinerary.reduce((a,l)=>a+l.pax,0);
  const ini = ((transaction.preferredName || transaction.firstName || "?")[0] + (transaction.lastName || " ")[0]).toUpperCase();

  const dayLines = days.map(dd => ({
    date: dd,
    lines: itinerary.filter(l=>l.date===dd).sort((a,b)=>a.time.localeCompare(b.time)).map(l => ({
      uid: l.uid,
      title: l.title,
      pax: l.pax,
      time: l.time,
      priceMinor: l.priceMinor,
      waitlist: l.waitlist,
    })),
  }));

  const settlementMethod = settlementResult?.paymentMethod || createResult.paymentMethod;
  const settlementBookingStatus = settlementResult?.bookingStatus || createResult.bookingStatus;
  const settlementPaymentStatus = settlementResult?.paymentStatus || createResult.paymentStatus;

  const ticks: WalkInDoneTick[] = [];
  if(transaction.email){
    ticks.push({
      cls:"wim-pend",
      name:"Email not sent",
      sub:`${transaction.email} · booking creation returned no email-delivery result.`,
    });
  }
  if(transaction.phone){
    ticks.push({
      cls:"wim-pend",
      name:"WhatsApp not sent",
      sub:`${transaction.phone} · booking creation returned no WhatsApp-delivery result.`,
    });
  }
  ticks.push({
    cls:"wim-pend",
    name:"Self-completion link not sent",
    sub:"Booking creation returned no self-completion token or send result.",
  });

  if(settlementMethod === "hold"){
    ticks.push({
      cls:"",
      name:"Time-sensitive hold created",
      sub:`Booking ${settlementBookingStatus || "status not returned"} · payment ${settlementPaymentStatus || "status not returned"}${createResult.holdExpiresAt ? ` · expires ${createResult.holdExpiresAt}` : ""}.`,
    });
  } else if(settlementMethod === "cash" || settlementMethod === "card_terminal"){
    const settlementLabel = settlementMethod === "cash" ? "Cash at Register" : "Card on Terminal";
    const settlementRecorded = settlementBookingStatus === "confirmed" && settlementPaymentStatus === "paid";
    ticks.push({
      cls:settlementRecorded ? "" : "wim-pend",
      name:settlementRecorded ? "Payment recorded" : "Settlement response received",
      sub:`${settlementLabel} · booking ${settlementBookingStatus || "status not returned"} · payment ${settlementPaymentStatus || "status not returned"}.`,
    });
  } else {
    ticks.push({
      cls:"wim-pend",
      name:"Settlement state not returned",
      sub:"The backend response did not return a recognized settlement method.",
    });
  }

  ticks.push({
    cls:"wim-pend",
    name:"Invoice not generated",
    sub:"Booking creation and settlement returned no invoice-generation result.",
  });
  ticks.push({
    cls:"",
    name:`Booking ${ref} created`,
    sub:`Source ${createResult.source || "not returned"} · booking ${createResult.bookingStatus || "status not returned"} · payment ${createResult.paymentStatus || "status not returned"}.`,
  });

  setDoneData({
    ref,
    nm,
    full,
    ini,
    email: transaction.email,
    phone: transaction.phone,
    nationality: transaction.nationality,
    pax,
    days: dayLines,
    ticks,
    totals: t,
    payMethod,
  });
  window.scrollTo({top:0,behavior:"smooth"});
}

function resetAll(){
  setItinerary([]);
  setPartyState(1);
  setPromo(null);
  setPayMethod("");
  setWeekOffset(0);
  setSelDate(today);
  setCalBase(null);
  setDoneData(null);
  setManifestDrafts({});
  setIdentityReviewed({});
  setManifestLevels({});
  setMedicalStates({});
  setMatchedDivers({});
  setDiverLookupTerms({});
  setDiverLookupResults({});
  setCreatedBookingId(null);
  setSettlementFailure(null);
  setCreateErr("");
  resetStep1();
}

function renderDoneTick(tick: WalkInDoneTick){
  return (
    <div className="wim-dfired" key={`${tick.name}-${tick.sub}`}>
      <div className={`wim-tick ${tick.cls}`.trim()}>
        {tick.cls==="wim-arrow" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" width="9" height="9"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        ) : tick.cls==="wim-pend" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" width="9" height="9"><path d="M12 6v6l4 2"/></svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><path d="M20 6L9 17l-5-5"/></svg>
        )}
      </div>
      <div><div className="wim-fn">{tick.name}</div><div className="wim-fs">{tick.sub}</div></div>
    </div>
  );
}

function renderDoneScreen(){
  if(!doneData) return null;
  return (
    <div className="wim-done" id="doneScreen" style={{display:"block"}}>
      <div className="wim-done-top">
        <div className="wim-done-i"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><path d="M20 6L9 17l-5-5"/></svg></div>
        <div className="wim-done-t">Booked &mdash; {money(doneData.totals.grand, operator.currency)}</div>
        <div className="wim-done-s">{itinerary.length} line{itinerary.length>1?'s':''} &middot; {doneData.pax} diver spot{doneData.pax>1?'s':''} &middot; {doneData.days.length} day{doneData.days.length>1?'s':''} &middot; {doneData.full}</div>
      </div>

      <div className="wim-done-grid">
        <div className="wim-dpane">
          <div className="wim-dpane-h">What you booked</div>
          <div className="wim-dpane-b">
            <div className="wim-dcust">
              <div className="wim-av" style={{width:"32px",height:"32px",fontSize:"11px",borderRadius:"8px"}}>{doneData.ini}</div>
              <div><div className="wim-cn">{doneData.full}</div><div className="wim-cm">{[doneData.email, doneData.phone, doneData.nationality ? natLabel(doneData.nationality) : null].filter(Boolean).join(' · ')}</div></div>
            </div>
            {doneData.days.map(day=>{
              const p = dparts(day.date);
              return (
                <div key={day.date}>
                  <div className="wim-dday">{p.wd} {p.mon} {p.dnum}</div>
                  {day.lines.map(line => (
                    <div className="wim-dline" key={line.uid}>
                      <span className="wim-dn">{line.title}{line.pax>1 ? <> &times;{line.pax}</> : null}</span>
                      <span className="wim-dtm">{t12(line.time)}</span>
                      <span className={`wim-da ${line.waitlist?'wim-free':''}`.trim()}>{line.waitlist?'Waitlist':money(line.priceMinor*line.pax, operator.currency)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            <div className="wim-dtot">
              <div className="wim-trow"><span>Subtotal</span><span className="wim-tv">{money(doneData.totals.subtotal, operator.currency)}</span></div>
              {doneData.totals.promoAmt ? <div className="wim-trow wim-disc"><span>Promo {promo?.label}</span><span className="wim-tv">&minus;{money(doneData.totals.promoAmt, operator.currency)}</span></div> : null}
              <div className="wim-trow wim-grand"><span>Total</span><span className="wim-tv">{money(doneData.totals.grand, operator.currency)}</span></div>
            </div>
            <div className="wim-dpay"><b>Payment:</b> {
              doneData.payMethod==='stripe' ? <>Stripe payment link. The booking is <b>pending</b> until the webhook confirms &mdash; the webhook is the only thing that marks it paid.</>
              : doneData.payMethod==='cash' ? <>Cash at the counter. Recorded <b>paid</b>.</>
              : doneData.payMethod==='hold' ? <>Created as a time-sensitive hold. No payment was recorded.</>
              : doneData.payMethod==='card_terminal' ? <>Card at your own terminal. Recorded <b>paid</b>.</>
              : null}</div>
          </div>
        </div>

        <div className="wim-dpane">
          <div className="wim-dpane-h">What just happened</div>
          <div className="wim-dpane-b">{doneData.ticks.map(renderDoneTick)}</div>
        </div>
      </div>

      <div className="wim-done-acts">
        <button className="wim-refbtn" onClick={() => alert(`Routes to Bookings → Booking Detail for ${doneData.ref}.\n\nThat surface already exists. Nothing new is built here.`)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M9 11H5a2 2 0 0 0-2 2v7h6zM15 7h-6v13h6zM21 4h-6v16h6z"/></svg>
          {doneData.ref}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="12" height="12"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
        </button>
        <button className="wim-btn wim-primary" onClick={resetAll}>New booking</button>
      </div>
    </div>
  );
}

function renderStep1Body(){
    if(step1Mode === "chip" && customer){
      const nm = `${customer.preferred_name||customer.first_name} ${customer.last_name||''}`
      const meta = [customer.email, customer.phone, natLabel(customer.nationality || "")].filter(Boolean).join(' · ')
      const ini = ((customer.preferred_name||customer.first_name||'?')[0] + (customer.last_name||' ')[0]).toUpperCase()

      return (
        <div className="wim-custchip">
          <div className="wim-av">{ini}</div>
          <div><div className="wim-cn">{nm}</div><div className="wim-cm">{meta}</div></div>
          <div className="wim-sp"></div>
          <button className="wim-btn" onClick={resetStep1}>Change</button>
        </div>
      )
    }

    if(step1Mode === "capture"){
      return (
        <>
          <div className="wim-row2">
            <div className="wim-fgrp"><label className="wim-fl">Legal first name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Jonathan" value={newCustomerDraft.fn} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, fn: e.target.value})} /></div>
            <div className="wim-fgrp"><label className="wim-fl">Legal last name <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="Robinson" value={newCustomerDraft.ln} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, ln: e.target.value})} /></div>
          </div>
          <div className="wim-row3">
            <div className="wim-fgrp"><label className="wim-fl">Preferred name</label><input className="wim-inp" placeholder="John" value={newCustomerDraft.pn} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, pn: e.target.value})} /></div>
            <div className="wim-fgrp"><label className="wim-fl">Date of birth <span className="wim-req">* REQ</span></label><input className="wim-inp" type="date" value={newCustomerDraft.dob} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, dob: e.target.value})} /></div>
            <div className="wim-fgrp"><label className="wim-fl">Nationality <span className="wim-req">* REQ</span></label>
              <select className="wim-inp" value={newCustomerDraft.nat} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, nat: e.target.value})}>
                <option value="">Select</option>
                {nationalities.map((x) => <option key={x.c} value={x.c}>{x.n}</option>)}
              </select></div>
          </div>
          <div className="wim-row2">
            <div className="wim-fgrp"><label className="wim-fl">Gender <span className="wim-req">* REQ</span></label>
              <select className="wim-inp" value={newCustomerDraft.gen} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, gen: e.target.value})}><option value="">Select</option><option value="male">Male</option><option value="female">Female</option></select></div>
            <div className="wim-fgrp"></div>
          </div>
          <p className="wim-hint">A first name and a last name are both required, and neither has to be unique — three divers named Jeff T. on one board is correct, and staff tell them apart by looking. The preferred name is shown exactly as entered, never parsed, split, or corrected. Nationality and gender are carried onto the dive manifest. Egypt requires both, and its form accepts exactly two genders and rejects the file otherwise. Nationality is stored as an ISO country code; each authority is served its own word for it.</p>
          <div className="wim-pairwrap" style={{marginTop: "12px"}}>
            <div className="wim-row2">
              <div className="wim-fgrp" style={{margin: 0}}><label className="wim-fl">Email <span className="wim-req">* REQ</span></label><input className="wim-inp" placeholder="name@email.com" value={newCustomerDraft.em} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, em: e.target.value})} /></div>
              <div className="wim-fgrp" style={{margin: 0}}><label className="wim-fl">Phone <span className="wim-opt">encouraged</span></label><input className="wim-inp" placeholder="+962 79 000 0000" value={newCustomerDraft.ph} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, ph: e.target.value})} /></div>
            </div>
            <p className="wim-pairnote"><b>Email is required. Phone is strongly encouraged.</b> Email is the only unique anchor on a customer record — <code>guest_identities_email_lower_unique</code> — and <code>dive_bookings_guest_or_diver_check</code> refuses the booking without one. The phone is indexed but not unique, so it cannot identify anybody on its own. Pathfinder revisits this when the diver owns the record.</p>
          </div>
          <div className="wim-row2" style={{marginTop: "12px"}}>
            <div className="wim-fgrp"><label className="wim-fl">Identity document <span className="wim-req">required before booking</span></label>
              <select className="wim-inp" value={newCustomerDraft.idt} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, idt: e.target.value})}><option value="passport">Passport</option><option value="national_id">National ID</option></select></div>
            <div className="wim-fgrp"><label className="wim-fl">Document number <span className="wim-req">required before booking</span></label>
              <input className="wim-inp" placeholder="Enter the official document number" value={newCustomerDraft.idn} onChange={(e) => setNewCustomerDraft({...newCustomerDraft, idn: e.target.value})} /></div>
          </div>
          <p className="wim-hint">You may continue building the itinerary while information is being gathered. Book Now will require the official document type and number before creating the booking.</p>
          <div style={{marginTop: "14px", display: "flex", gap: "9px"}}><button className="wim-btn wim-primary" onClick={saveNew}>Save customer</button><button className="wim-btn" onClick={resetStep1}>Back to search</button></div>
        </>
      )
    }

    return (
      <>
        <div className="wim-searchrow">
          <input className="wim-inp" type="text" placeholder="Phone, email, or name — try “robinson”" autoComplete="off" value={lookup} onChange={(e) => setLookup(e.target.value)} onKeyDown={(event) => { if(event.key === "Enter"){ event.preventDefault(); void doLookup(); } }} />
          <button className="wim-btn wim-primary" onClick={() => { void doLookup(); }}>Search</button>
        </div>
        <p className="wim-hint">Returning is earned by a completed dive, never set by hand. If they have dived with you, they are findable here.</p>
        <div>
          {step1Mode === "match" ? (
            lookupResults.length ? (
              <>
                {lookupResults.map((match) => {
                  const matchName = `${match.preferred_name||match.first_name} ${match.last_name||''}`.trim();
                  const matchInitials = ((match.preferred_name||match.first_name||'?')[0] + (match.last_name||' ')[0]).toUpperCase();
                  const matchMeta = [match.phone, match.email, match.last_booking_date ? `last booking: ${match.last_booking_date}` : null].filter(Boolean).join(' · ');
                  return (
                    <div className="wim-match" key={match.guest_id || match.email || matchName}>
                      <div className="wim-av">{matchInitials}</div>
                      <div className="wim-match-b">
                        <div className="wim-st">✓ Returning — completed a dive</div>
                        <div className="wim-nm">{matchName}</div>
                        <div className="wim-mt">{matchMeta}</div>
                      </div>
                      <button className="wim-btn wim-primary" onClick={() => pickReturning(match)}>Use this customer</button>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="wim-match wim-newc">
                <div className="wim-av" style={{background: "var(--full)"}}>?</div>
                <div className="wim-match-b">
                  <div className="wim-st">No match</div>
                  <div className="wim-nm">New diver — enter them in the Manifest below</div>
                  <div className="wim-mt">No need to capture here. Build the itinerary, then add them as Diver 1. Or capture now if you prefer.</div>
                </div>
                <button className="wim-btn" onClick={newCustomer}>Capture here</button>
              </div>
            )
          ) : null}
        </div>
      </>
    )
  }

  const hasLines = itinerary.length > 0
  const lockedClass = hasLines ? "wim-step" : "wim-step wim-locked"

  return (
    <div className="wim-page">
      <div className="wim-crown">
        <div>
          <div className="wim-eyebrow">Operations</div>
          <h1>Book Now</h1>
          <p>Every booking that does not come through the widget. Build it, take the money, confirm it — without leaving this page.</p>
        </div>
      </div>

      <div className="wim-stage" id="stage" style={{display:doneData ? "none" : undefined}}>
        <div className="wim-work">
          <section className="wim-step" id="step1">
            <div className="wim-stephead">
              <div className={customer ? "wim-stepnum wim-done" : "wim-stepnum"} id="s1n">1</div>
              <div className="wim-steptitle">Returning diver?<span className="wim-stepsub">Optional — search to pull in a past diver, or skip ahead and enter them in the Manifest.</span></div>
            </div>
            <div className="wim-stepbody">{renderStep1Body()}</div>
          </section>

          {/* STEP 2 — ITINERARY. Never locked. "What is your schedule like?"
               is the most common sentence at the counter and it arrives before a name does. */}
          <section className="wim-step" id="step2">
            <div className="wim-stephead">
              <div className="wim-stepnum" id="s2n">2</div>
              <div className="wim-steptitle">Itinerary<span className="wim-stepsub">build the adventure — dives and courses across days</span></div>
            </div>
            <div className="wim-stepbody">
              <div className="wim-party">
                <div className="wim-pl">How many divers on each line you add?</div>
                <div className="wim-stepper"><button onClick={() => setParty(-1)}>&minus;</button><span id="partyN">{party}</span><button onClick={() => setParty(1)}>+</button></div>
              </div>
              <div className="wim-dates-row">
                <button className="wim-date-nav" onClick={() => shiftWeek(-1)} aria-label="Previous week"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="15" height="15"><path d="M15 5l-7 7 7 7"/></svg></button>
                <div className="wim-datescroll" id="dates">{renderDates()}</div>
                <button className="wim-date-nav" onClick={() => shiftWeek(1)} aria-label="Next week"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="15" height="15"><path d="M9 5l7 7-7 7"/></svg></button>
                <button className="wim-calbtn" onClick={openCal}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18M8 2v4M16 2v4"/></svg> Calendar</button>
              </div>
              <div className="wim-sesslist" id="sessions">{renderSessions()}</div>
            </div>
          </section>

          <section className={lockedClass} id="step3">
            <div className="wim-stephead">
              <div className="wim-stepnum" id="s3n">3</div>
              <div className="wim-steptitle">Manifest<span className="wim-stepsub">who is diving — Diver 1 is the lead and the customer on the booking</span></div>
            </div>
            <div className="wim-stepbody" id="manifest">{renderManifest()}</div>
          </section>

          <section className={lockedClass} id="step4">
            <div className="wim-stephead">
              <div className="wim-stepnum" id="s4n">4</div>
              <div className="wim-steptitle">Payment<span className="wim-stepsub">how is the customer paying?</span></div>
            </div>
            <div className="wim-stepbody" id="payment">{renderPayment()}</div>
          </section>

          <section className={lockedClass} id="step5">
            <div className="wim-stephead">
              <div className="wim-stepnum" id="s5n">5</div>
              <div className="wim-steptitle">Confirm and notify<span className="wim-stepsub">what happens the moment you close this booking</span></div>
            </div>
            <div className="wim-stepbody" id="confirm">{renderConfirm()}</div>
          </section>
        </div>

        <div className="wim-railcol">
          {renderRail()}
          <aside className="wim-owpanel" id="owPanel">{renderOnWater()}</aside>
        </div>
      </div>

      {/* ============ DONE ============ */}
      {renderDoneScreen()}

      {/* ============ CALENDAR OVERLAY ============ */}
      <div className={calOpen ? "wim-calmask wim-on" : "wim-calmask"} id="calMask" onClick={(event) => { if(event.target===event.currentTarget) closeCal(); }}>
        <div className="wim-calbox" role="dialog" aria-label="Choose a day">
          <div className="wim-calhead">
            <div className="wim-calttl" id="calTitle">{calBase ? `${MONTHS[dt(calBase).getUTCMonth()]} ${dt(calBase).getUTCFullYear()}` : "Choose a day"}</div>
            <div className="wim-calnav">
              <button onClick={() => calShift(-1)} aria-label="Previous month"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="13" height="13"><path d="M15 5l-7 7 7 7"/></svg></button>
              <button onClick={() => calShift(1)} aria-label="Next month"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" width="13" height="13"><path d="M9 5l7 7-7 7"/></svg></button>
            </div>
            <button className="wim-calx" onClick={() => closeCal()} aria-label="Close">&times;</button>
          </div>
          <div className="wim-calwrap">
            <div id="calMount">{renderCal()}</div>
            <div className="wim-callegend">
              <div className="wim-lg"><span className="wim-lgd" style={{background:"var(--teal)"}}></span> Space</div>
              <div className="wim-lg"><span className="wim-lgd" style={{background:"var(--gold)"}}></span> Filling up</div>
              <div className="wim-lg"><span className="wim-lgd" style={{background:"var(--rare)"}}></span> Waitlist</div>
              <div className="wim-lg"><span className="wim-lgd" style={{background:"var(--surface)",border:"1px solid var(--line)"}}></span> Nothing running</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
